import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, message, Modal, Select, Space, Table, Tooltip } from "antd";
import {
    DisconnectOutlined,
    EyeOutlined,
    LinkOutlined,
    PaperClipOutlined,
    PlusOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { FileDown, Sparkles } from "lucide-react";

import DocumentProcessor from "./DocumentProcessor/DocumentProcessor";
import DetailOffer from "./DetailOffer";
import { fetchOffers } from "../api/offersApi";
import {
    fetchOrderOffers,
    linkOfferToOrder,
    unlinkOfferFromOrder
} from "../api/orderOffersApi";
import styles from "./OrderOffersModal.module.css";

const OrderOffersModal = ({ open, order, onClose, setOffers, catalogLinks = false }) => {
    const [activeTab, setActiveTab] = useState("list");
    const [linkedOffers, setLinkedOffers] = useState([]);
    const [allOffers, setAllOffers] = useState([]);
    const [selectedOfferId, setSelectedOfferId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [linking, setLinking] = useState(false);
    const [reportLoading, setReportLoading] = useState("");
    const [detailOffer, setDetailOffer] = useState(null);

    const loadOffers = useCallback(async () => {
        if (!order?.id) return;
        setLoading(true);
        try {
            const [linked, all] = await Promise.all([
                fetchOrderOffers(order.id),
                fetchOffers()
            ]);
            setLinkedOffers(linked);
            setAllOffers(all);
            setOffers?.(all);
        } catch (error) {
            message.error(error.message || "Не удалось загрузить предложения");
        } finally {
            setLoading(false);
        }
    }, [order?.id, setOffers]);

    useEffect(() => {
        if (!open) return;
        setActiveTab(order?.initialTab === "upload" ? "upload" : "list");
        setSelectedOfferId(null);
        loadOffers();
    }, [open, loadOffers, order?.initialTab]);

    useEffect(() => {
        if (!open) return;
        window.addEventListener("chipdip-completed", loadOffers);
        return () => window.removeEventListener("chipdip-completed", loadOffers);
    }, [open, loadOffers]);

    const availableOffers = useMemo(
        () => allOffers.filter(offer => offer.orderId == null),
        [allOffers]
    );

    const handleLink = async () => {
        if (!selectedOfferId) return;
        setLinking(true);
        try {
            await linkOfferToOrder(order.id, selectedOfferId);
            message.success("Предложение привязано к запросу");
            setSelectedOfferId(null);
            await loadOffers();
        } catch (error) {
            message.error(error.message || "Не удалось привязать предложение");
        } finally {
            setLinking(false);
        }
    };

    const handleUnlink = (offer) => {
        Modal.confirm({
            title: "Отвязать предложение?",
            content: "Само предложение останется сохранённым в системе.",
            okText: "Отвязать",
            cancelText: "Отмена",
            onOk: async () => {
                try {
                    await unlinkOfferFromOrder(order.id, offer.id);
                    message.success("Связь удалена");
                    await loadOffers();
                } catch (error) {
                    message.error(error.message || "Не удалось отвязать предложение");
                }
            }
        });
    };

    const handleDocumentSaved = async (savedOffer) => {
        if (savedOffer?.id) {
            setLinkedOffers(current => [
                savedOffer,
                ...current.filter(offer => String(offer.id) !== String(savedOffer.id))
            ]);
        }
        await loadOffers();
        setActiveTab("list");
    };

    const downloadReport = async (url, kind) => {
        setReportLoading(kind);
        try {
            const response = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    order: order.id,
                    orderName: order.name,
                    offers: linkedOffers.map(offer => offer.id)
                })
            });

            if (!response.ok) {
                const details = await response.text().catch(() => "");
                throw new Error(details || "Не удалось сформировать отчёт");
            }

            const blobUrl = URL.createObjectURL(await response.blob());
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = `${order.name}_${kind}.xlsx`;
            link.click();
            setTimeout(() => URL.revokeObjectURL(blobUrl), 200);
        } catch (error) {
            message.error(error.message || "Не удалось сформировать отчёт");
        } finally {
            setReportLoading("");
        }
    };

    const columns = [
        {
            title: "ID",
            dataIndex: "id",
            width: 80
        },
        {
            title: "Поставщик",
            dataIndex: "name",
            render: (name, offer) => (
                <button
                    type="button"
                    className={styles.offerName}
                    onClick={() => setDetailOffer(offer)}
                >
                    {name}
                </button>
            )
        },
        {
            title: "Сотрудник",
            key: "employee",
            render: (_, offer) => offer.employeeName || offer.employee_name || "—"
        },
        {
            title: "Дата загрузки",
            dataIndex: "created",
            render: value => value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "—"
        },
        {
            title: "Действия",
            key: "actions",
            width: 150,
            render: (_, offer) => (
                <Space>
                    <Tooltip title="Детализация">
                        <Button
                            icon={<EyeOutlined />}
                            onClick={() => setDetailOffer(offer)}
                        />
                    </Tooltip>
                    <Tooltip title="Отвязать без удаления">
                        <Button
                            icon={<DisconnectOutlined />}
                            onClick={() => handleUnlink(offer)}
                            aria-label="Отвязать предложение без удаления"
                        />
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <>
            <Modal
                open={open && !detailOffer}
                onCancel={onClose}
                footer={null}
                width={1200}
                destroyOnClose
                title={`Предложения по запросу: ${order?.name || ""}`}
            >
                <div className={styles.modalBody}>
                    <div className={styles.tabs}>
                        <button
                            type="button"
                            className={`${styles.tab} ${activeTab === "list" ? styles.tabActive : ""}`}
                            onClick={() => setActiveTab("list")}
                        >
                            <PaperClipOutlined /> Связанные ({linkedOffers.length})
                        </button>
                        <button
                            type="button"
                            className={`${styles.tab} ${activeTab === "upload" ? styles.tabActive : ""}`}
                            onClick={() => setActiveTab("upload")}
                        >
                            <PlusOutlined /> Загрузить предложение
                        </button>
                        {linkedOffers.length > 0 && (
                            <div className={styles.reportActions}>
                                <Button
                                    icon={<FileDown size={16} />}
                                    loading={reportLoading === "comparison"}
                                    disabled={Boolean(reportLoading)}
                                    onClick={() => downloadReport("/api/generateReport", "comparison")}
                                >
                                    Сравнение цен
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<Sparkles size={16} />}
                                    loading={reportLoading === "best-offer"}
                                    disabled={Boolean(reportLoading)}
                                    onClick={() => downloadReport("/api/generateBestOffer", "best-offer")}
                                >
                                    Лучшее предложение
                                </Button>
                            </div>
                        )}
                    </div>

                    {activeTab === "list" ? (
                        <>
                            <div className={styles.toolbar}>
                                <div className={styles.selectGroup}>
                                    <label htmlFor="existing-offer">Привязать существующее предложение</label>
                                    <Select
                                        id="existing-offer"
                                        showSearch
                                        allowClear
                                        value={selectedOfferId}
                                        onChange={setSelectedOfferId}
                                        placeholder="Выберите предложение без связи"
                                        optionFilterProp="label"
                                        options={availableOffers.map(offer => ({
                                            value: offer.id,
                                            label: `${offer.name} · ID ${offer.id}`
                                        }))}
                                        notFoundContent="Предложений без связи нет"
                                    />
                                </div>
                                <Button
                                    type="primary"
                                    icon={<LinkOutlined />}
                                    disabled={!selectedOfferId}
                                    loading={linking}
                                    onClick={handleLink}
                                >
                                    Привязать
                                </Button>
                            </div>

                            {linkedOffers.length === 0 && !loading ? (
                                <div className={styles.empty}>
                                    У этого запроса пока нет связанных предложений.
                                </div>
                            ) : (
                                <Table
                                    rowKey="id"
                                    size="small"
                                    loading={loading}
                                    columns={columns}
                                    dataSource={linkedOffers}
                                    pagination={false}
                                    scroll={{ x: 760 }}
                                />
                            )}
                        </>
                    ) : (
                        <div className={styles.processor}>
                            <DocumentProcessor
                                lockedDocumentType="offer"
                                linkedOrderId={order?.id}
                                fileNamePrefix={order?.name}
                                setOffers={setOffers}
                                onDocumentSaved={handleDocumentSaved}
                            />
                        </div>
                    )}
                </div>
            </Modal>

            <DetailOffer
                catalogLinks={catalogLinks}
                isOpen={Boolean(detailOffer)}
                selectedFileIds={detailOffer?.id ? [detailOffer.id] : []}
                numberOffer={detailOffer?.name}
                onClose={() => setDetailOffer(null)}
            />
        </>
    );
};

export default OrderOffersModal;
